import {
  ITaskCompleted,
  ITaskHandle,
  ITaskList,
  ITaskUpdateExpiration,
} from "@defs/task";
import { ITicketSuspendRequest, ITicketSuspendResponse } from "@defs/ticket";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { ApiResponsePaginated } from "@defs/api-response";
import { PayloadActionFulfilledMeta } from "@defs/types";

interface TicketsState {
  creation: {
    loading: boolean;
  };

  list: {
    loading: boolean;
    data: ITaskList[];
    total: number;
    currentRequestId: string;
    searchingProgressive: boolean;
  };

  previewIndex: number;
}

const initialState: TicketsState = {
  creation: {
    loading: false,
  },

  list: {
    loading: true,
    data: [],
    total: null,
    currentRequestId: undefined,
    searchingProgressive: false,
  },

  previewIndex: null,
};

const ticketsSlice = createSlice({
  name: "tickets",
  initialState,
  reducers: {
    reset(state) {
      state.list = initialState.list;
    },
  },
  extraReducers: {
    "tickets/list/pending": (
      state,
      action: PayloadAction<number, string, PayloadActionFulfilledMeta<number>>
    ) => {
      state.list.loading = true;
      state.list.currentRequestId = action.meta.requestId;
    },

    "tickets/list/fulfilled": (
      state,
      action: PayloadAction<
        ApiResponsePaginated<ITaskList[]>,
        string,
        PayloadActionFulfilledMeta<string>
      >
    ) => {
      const { currentRequestId: current } = state.list;
      const { requestId } = action.meta;

      if (current === requestId) {
        const { data, meta } = action.payload;

        state.list.loading = false;
        state.list.data = data;
        state.list.total = meta.total;
        state.list.currentRequestId = undefined;
      }
    },

    "tickets/searchProgressive/pending": (state) => {
      state.list.searchingProgressive = true;
    },

    "tickets/searchProgressive/fulfilled": (state) => {
      state.list.searchingProgressive = false;
    },

    "tickets/searchProgressive/rejected": (state) => {
      state.list.searchingProgressive = false;
    },

    "tickets/create/pending": (state) => {
      state.creation.loading = true;
    },

    "tickets/create/fulfilled": (state) => {
      state.creation.loading = false;
    },

    "tickets/create/rejected": (state) => {
      state.creation.loading = false;
    },

    "tasks/handle/fulfilled": (
      state,
      action: PayloadAction<
        ITaskHandle,
        string,
        PayloadActionFulfilledMeta<string>
      >
    ) => {
      if (state.list.data.length) {
        const id = action.meta.arg;
        const { handler, handled_at } = action.payload;

        state.list.data = state.list.data.map((t: ITaskList) => {
          if (t.id === id) {
            return { ...t, handler, handled_at };
          }

          return t;
        });
      }
    },

    "tasks/complete/fulfilled": (
      state,
      action: PayloadAction<ITaskCompleted>
    ) => {
      const { task } = action.payload;

      state.list.total = state.list.total - 1;
      state.list.data = state.list.data.filter((item) => item.id !== task.id);
    },

    "tasks/delete/fulfilled": (
      state,
      action: PayloadAction<string, string, PayloadActionFulfilledMeta<string>>
    ) => {
      if (state.list.data.length) {
        const id = action.meta.arg;
        state.list.data = state.list.data.filter((t) => t.id !== id);
      }
    },

    "ticket/suspend/fulfilled": (
      state,
      action: PayloadAction<
        ITicketSuspendResponse,
        string,
        PayloadActionFulfilledMeta<ITicketSuspendRequest>
      >
    ) => {
      if (state.list.data.length) {
        const { suspend } = action.payload;
        const suspendedAt = suspend ? new Date().toISOString() : null;

        state.list.data = state.list.data.map((ticket) => {
          if (ticket.id === action.meta.arg.id) {
            const status = suspend ? "Sospeso" : "Preso in carico";

            return { ...ticket, status, suspendedAt };
          }

          return ticket;
        });
      }
    },

    "ticket/delete/fulfilled": (
      state,
      action: PayloadAction<
        string,
        string,
        PayloadActionFulfilledMeta<{ id: string }>
      >
    ) => {
      if (state.list.data.length) {
        state.list.data = state.list.data.filter(
          (task) => task.ticket.id !== action.meta.arg.id
        );
      }
    },

    "task/expiration/update/fulfilled": (
      state,
      action: PayloadAction<
        string,
        string,
        PayloadActionFulfilledMeta<ITaskUpdateExpiration>
      >
    ) => {
      if (state.list.data.length) {
        const { id } = action.meta.arg;
        const expires_at = action.payload;

        state.list.data = state.list.data.map((t) => {
          if (t.id === id) {
            return { ...t, expires_at };
          }

          return t;
        });
      }
    },
  },
});

export const ticketsActions = ticketsSlice.actions;

export default ticketsSlice.reducer;
